import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Alert,
  Button,
  Card,
  Col,
  Drawer,
  Empty,
  Input,
  Modal,
  Progress,
  Radio,
  Row,
  Segmented,
  Select,
  Space,
  Spin,
  Statistic,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import {
  ArrowDownOutlined,
  ArrowLeftOutlined,
  ArrowUpOutlined,
  CopyOutlined,
  DownloadOutlined,
  ExperimentOutlined,
  ReloadOutlined,
  TeamOutlined,
  TrophyOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useAppDispatch, useAppSelector } from 'store';
import {
  completeABTest,
  fetchABTestReport,
  fetchSendStatus,
  selectABTestMetric,
  selectABTestReport,
  selectABTestReportLoading,
  selectABTestSendJob,
  setMetric,
} from 'store/slices/abTest.slice';
import { abTestApi } from 'api/endpoints/ab_test.api';
import {
  ABMetric,
  ABTestRecipient,
  ABTestStatus,
  ABVariantReport,
  RecipientStatus,
} from 'types/ab_test.types';
import { notify } from 'utils/helpers';
import { ROUTES } from 'config/routes';

const { Title, Text, Paragraph } = Typography;
const { confirm } = Modal;

const STATUS_META: Record<ABTestStatus, { color: string; label: string }> = {
  [ABTestStatus.DRAFT]: { color: 'default', label: 'Черновик' },
  [ABTestStatus.RUNNING]: { color: 'processing', label: 'Активен' },
  [ABTestStatus.PAUSED]: { color: 'warning', label: 'Пауза' },
  [ABTestStatus.COMPLETED]: { color: 'success', label: 'Завершён' },
  [ABTestStatus.CANCELLED]: { color: 'error', label: 'Отменён' },
};

const VARIANT_COLORS = ['#1890ff', '#52c41a', '#faad14', '#eb2f96', '#13c2c2', '#722ed1'];

const fmtPct = (v?: number | null, digits = 2) =>
  v === null || v === undefined || Number.isNaN(v) ? '—' : `${v.toFixed(digits)}%`;

const fmtInt = (v?: number | null) =>
  v === null || v === undefined ? '—' : v.toLocaleString('ru-RU');

const fmtDateTime = (v?: string | null) =>
  v ? new Date(v).toLocaleString('ru-RU', { dateStyle: 'short', timeStyle: 'short' }) : '—';

const RateBar: React.FC<{
  rate: number;
  low: number;
  high: number;
  max: number;
  color: string;
}> = ({ rate, low, high, max, color }) => {
  const scale = (v: number) => (max > 0 ? Math.min(100, (v / max) * 100) : 0);
  return (
    <div style={{ position: 'relative', height: 18, marginTop: 4 }}>
      <div
        style={{
          position: 'absolute',
          left: `${scale(low)}%`,
          width: `${Math.max(1, scale(high) - scale(low))}%`,
          top: 6,
          height: 6,
          background: color,
          opacity: 0.25,
          borderRadius: 3,
        }}
      />
      <div
        style={{
          position: 'absolute',
          left: `calc(${scale(rate)}% - 4px)`,
          top: 3,
          width: 8,
          height: 12,
          background: color,
          borderRadius: 2,
        }}
      />
    </div>
  );
};

const ABTestResults: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const testId = Number(id);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const report = useAppSelector(selectABTestReport);
  const loading = useAppSelector(selectABTestReportLoading);
  const metric = useAppSelector(selectABTestMetric);
  const sendJob = useAppSelector(selectABTestSendJob);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const timerRef = useRef<number>();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [recipients, setRecipients] = useState<ABTestRecipient[]>([]);
  const [recTotal, setRecTotal] = useState(0);
  const [recLoading, setRecLoading] = useState(false);
  const [recVariant, setRecVariant] = useState<number>(0);
  const [recStatus, setRecStatus] = useState<RecipientStatus>('');
  const [recSearch, setRecSearch] = useState('');
  const [recPage, setRecPage] = useState(1);
  const [exporting, setExporting] = useState(false);

  const load = useCallback(() => {
    if (!testId) return;
    dispatch(fetchABTestReport({ id: testId, metric }));
  }, [dispatch, testId, metric]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!testId) return;
    dispatch(fetchSendStatus(testId));
  }, [dispatch, testId]);

  useEffect(() => {
    const isSending = sendJob?.status === 'running';
    if (!autoRefresh && !isSending) return;

    timerRef.current = window.setInterval(() => {
      load();
      if (isSending) dispatch(fetchSendStatus(testId));
    }, isSending ? 3000 : 15000);

    return () => window.clearInterval(timerRef.current);
  }, [autoRefresh, sendJob?.status, load, dispatch, testId]);

  const loadRecipients = useCallback(
    async (page = recPage) => {
      if (!testId) return;
      setRecLoading(true);
      try {
        const res = await abTestApi.getRecipients(testId, {
          variant_id: recVariant || undefined,
          status: recStatus || undefined,
          search: recSearch || undefined,
          limit: 50,
          offset: (page - 1) * 50,
        });
        setRecipients(res.recipients || []);
        setRecTotal(res.total || 0);
        setRecPage(page);
      } catch (e: any) {
        notify.error(e?.response?.data?.error || 'Не удалось загрузить получателей');
      } finally {
        setRecLoading(false);
      }
    },
    [testId, recVariant, recStatus, recSearch, recPage]
  );

  useEffect(() => {
    if (drawerOpen) loadRecipients(1);
  }, [drawerOpen, recVariant, recStatus]);

  const handleExport = async (variantId?: number) => {
    setExporting(true);
    try {
      await abTestApi.exportRecipients(testId, {
        variant_id: variantId,
        status: recStatus || undefined,
        search: recSearch || undefined,
      });
      notify.success('Выгрузка началась');
    } catch (e: any) {
      notify.error(e?.response?.data?.error || 'Не удалось выгрузить получателей');
    } finally {
      setExporting(false);
    }
  };

  const copyAfIds = async () => {
    const ids = recipients.map((r) => r.af_id).filter(Boolean);
    if (!ids.length) {
      notify.error('На этой странице нет af_id');
      return;
    }
    await navigator.clipboard.writeText(ids.join('\n'));
    message.success(`Скопировано af_id: ${ids.length}`);
  };

  const handleDeclareWinner = (variant: ABVariantReport) => {
    const risky = !variant.significant && !variant.is_control;
    confirm({
      title: `Объявить победителем «${variant.name}»?`,
      content: risky
        ? `Отличие от контроля пока не значимо (p = ${variant.p_value?.toFixed(3) ?? '—'}). ` +
          'Тест будет завершён, но вывод может оказаться случайным.'
        : 'Тест перейдёт в статус «Завершён», победитель будет зафиксирован.',
      okText: 'Завершить тест',
      okType: risky ? 'danger' : 'primary',
      cancelText: 'Отмена',
      onOk: async () => {
        try {
          await dispatch(
            completeABTest({ id: testId, winnerVariantId: variant.id, metric })
          ).unwrap();
          notify.success('Тест завершён');
          load();
        } catch (e: any) {
          notify.error(typeof e === 'string' ? e : 'Не удалось завершить тест');
        }
      },
    });
  };

  const maxRate = useMemo(
    () => Math.max(0.0001, ...(report?.variants || []).map((v) => v.ci_high || v.rate || 0)),
    [report]
  );

  const colorOf = useCallback(
    (variantId: number) => {
      const idx = (report?.variants || []).findIndex((v) => v.id === variantId);
      return VARIANT_COLORS[Math.max(0, idx) % VARIANT_COLORS.length];
    },
    [report]
  );

  if (loading && !report) {
    return (
      <div style={{ textAlign: 'center', padding: 100 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!report) {
    return (
      <Card>
        <Empty description="A/B тест не найден">
          <Button onClick={() => navigate(ROUTES.PUSH_CENTER)}>К списку тестов</Button>
        </Empty>
      </Card>
    );
  }

  const statusMeta = STATUS_META[report.status] || { color: 'default', label: report.status };
  const canDeclareWinner =
    report.status === ABTestStatus.RUNNING || report.status === ABTestStatus.PAUSED;

  const columns: ColumnsType<ABVariantReport> = [
    {
      title: 'Вариант',
      key: 'variant',
      width: 220,
      fixed: 'left',
      render: (_, r) => (
        <Space direction="vertical" size={2}>
          <Space size={6}>
            <span
              style={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                background: colorOf(r.id),
                display: 'inline-block',
              }}
            />
            <Text strong>{r.name}</Text>
            {r.is_control && <Tag>Контроль</Tag>}
            {report.winner_variant_id === r.id && (
              <Tooltip title="Зафиксированный победитель">
                <TrophyOutlined style={{ color: '#faad14' }} />
              </Tooltip>
            )}
          </Space>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {r.traffic_percentage}% трафика
          </Text>
        </Space>
      ),
    },
    {
      title: report.base === 'delivered' ? 'Доставлено' : 'Отправлено',
      key: 'base',
      width: 130,
      align: 'right',
      render: (_, r) => (
        <Space direction="vertical" size={0} style={{ alignItems: 'flex-end' }}>
          <Text>{fmtInt(r.base)}</Text>
          <Text type="secondary" style={{ fontSize: 11 }}>
            из {fmtInt(r.sent)} отпр.
          </Text>
        </Space>
      ),
    },
    {
      title: (
        <Tooltip title="Значение выбранной метрики и 95% доверительный интервал (Уилсон)">
          <span>{report.metric_title}</span>
        </Tooltip>
      ),
      key: 'rate',
      width: 230,
      render: (_, r) => (
        <div>
          <Space size={6}>
            <Text strong style={{ fontSize: 15 }}>
              {fmtPct(r.rate)}
            </Text>
            <Text type="secondary" style={{ fontSize: 11 }}>
              {fmtInt(r.conversions)} шт.
            </Text>
          </Space>
          <RateBar
            rate={r.rate}
            low={r.ci_low}
            high={r.ci_high}
            max={maxRate}
            color={colorOf(r.id)}
          />
          <Text type="secondary" style={{ fontSize: 11 }}>
            ДИ {fmtPct(r.ci_low)} … {fmtPct(r.ci_high)}
          </Text>
        </div>
      ),
    },
    {
      title: 'К контролю',
      key: 'uplift',
      width: 120,
      align: 'right',
      render: (_, r) => {
        if (r.is_control) return <Tag>База</Tag>;
        if (r.uplift === null || r.uplift === undefined) return <Text type="secondary">—</Text>;
        const up = r.uplift;
        const color = up > 0 ? '#52c41a' : up < 0 ? '#ff4d4f' : undefined;
        return (
          <Text style={{ color }}>
            {up > 0 ? <ArrowUpOutlined /> : up < 0 ? <ArrowDownOutlined /> : null}{' '}
            {up > 0 ? '+' : ''}
            {Number.isFinite(up) ? up.toFixed(1) : '∞'}%
          </Text>
        );
      },
    },
    {
      title: (
        <Tooltip title="Вероятность увидеть такую разницу, если варианты на самом деле одинаковы. Меньше — надёжнее.">
          <span>p-value</span>
        </Tooltip>
      ),
      key: 'p',
      width: 150,
      align: 'right',
      render: (_, r) => {
        if (r.is_control) return <Text type="secondary">—</Text>;
        if (r.p_value === null || r.p_value === undefined)
          return <Text type="secondary">мало данных</Text>;
        return (
          <Space direction="vertical" size={0} style={{ alignItems: 'flex-end' }}>
            <Text>{r.p_value < 0.0001 ? '< 0.0001' : r.p_value.toFixed(4)}</Text>
            {r.significant ? (
              <Tag color="success" style={{ marginInlineEnd: 0 }}>
                значимо
              </Tag>
            ) : (
              <Tag style={{ marginInlineEnd: 0 }}>не значимо</Tag>
            )}
          </Space>
        );
      },
    },
    {
      title: 'Воронка',
      key: 'funnel',
      width: 190,
      render: (_, r) => (
        <Space direction="vertical" size={0}>
          <Text style={{ fontSize: 12 }}>
            Доставка: {fmtInt(r.delivered)} ({fmtPct(r.delivery_rate, 1)})
          </Text>
          <Text style={{ fontSize: 12 }}>
            Клики: {fmtInt(r.clicked)} ({fmtPct(r.ctr, 2)})
          </Text>
          <Text style={{ fontSize: 12 }}>
            Рег {fmtInt(r.registrations)} · Деп {fmtInt(r.deposits)}
          </Text>
          {r.failed > 0 && (
            <Text type="danger" style={{ fontSize: 12 }}>
              Ошибок: {fmtInt(r.failed)}
            </Text>
          )}
        </Space>
      ),
    },
    {
      title: 'Действия',
      key: 'actions',
      width: 200,
      fixed: 'right',
      render: (_, r) => (
        <Space>
          <Tooltip title="Кто получил этот вариант">
            <Button
              size="small"
              icon={<TeamOutlined />}
              onClick={() => {
                setRecVariant(r.id);
                setDrawerOpen(true);
              }}
            />
          </Tooltip>
          <Tooltip title="Выгрузить af_id получателей в CSV">
            <Button
              size="small"
              icon={<DownloadOutlined />}
              loading={exporting}
              onClick={() => handleExport(r.id)}
            />
          </Tooltip>
          {canDeclareWinner && (
            <Button
              size="small"
              type={r.significant ? 'primary' : 'default'}
              icon={<TrophyOutlined />}
              onClick={() => handleDeclareWinner(r)}
            >
              Победитель
            </Button>
          )}
        </Space>
      ),
    },
  ];

  const recipientColumns: ColumnsType<ABTestRecipient> = [
    {
      title: 'Вариант',
      dataIndex: 'variant_name',
      width: 140,
      render: (name: string, r) => (
        <Space size={4}>
          <span
            style={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              background: colorOf(r.variant_id),
              display: 'inline-block',
            }}
          />
          {name}
        </Space>
      ),
    },
    {
      title: 'af_id',
      dataIndex: 'af_id',
      width: 300,
      render: (v: string, r) => (
        <Text copyable={!!v} style={{ fontFamily: 'monospace', fontSize: 12 }}>
          {v || r.external_id || `#${r.app_user_id}`}
        </Text>
      ),
    },
    { title: 'ОС', dataIndex: 'os', width: 90 },
    {
      title: 'Гео / язык',
      key: 'geo',
      width: 120,
      render: (_, r) => [r.country, r.locale].filter(Boolean).join(' / ') || '—',
    },
    {
      title: 'Отправлен',
      dataIndex: 'sent_at',
      width: 150,
      render: fmtDateTime,
    },
    {
      title: 'Результат',
      key: 'result',
      width: 240,
      render: (_, r) => (
        <Space size={4} wrap>
          {r.failed_at ? (
            <Tooltip title={fmtDateTime(r.failed_at)}>
              <Tag color="red">ошибка</Tag>
            </Tooltip>
          ) : r.delivered_at ? (
            <Tag color="blue">доставлен</Tag>
          ) : (
            <Tooltip title="Нет строки интеракции — пуш не дошёл до отправки в FCM">
              <Tag>нет данных</Tag>
            </Tooltip>
          )}
          {r.clicked_at && <Tag color="green">клик</Tag>}
          {r.attributed_registration && <Tag color="purple">рег</Tag>}
          {r.attributed_deposit && <Tag color="gold">деп</Tag>}
        </Space>
      ),
    },
  ];

  return (
    <div className="page-container">
      <Card style={{ marginBottom: 16 }}>
        <Row justify="space-between" align="middle" gutter={[16, 16]}>
          <Col flex="auto">
            <Space align="start">
              <Button
                icon={<ArrowLeftOutlined />}
                onClick={() => navigate(ROUTES.PUSH_CENTER)}
              />
              <ExperimentOutlined style={{ fontSize: 22, marginTop: 4 }} />
              <div>
                <Space size={8} wrap>
                  <Title level={4} style={{ margin: 0 }}>
                    {report.name}
                  </Title>
                  <Tag color={statusMeta.color}>{statusMeta.label}</Tag>
                </Space>
                <Space size={8} wrap>
                  {report.description && (
                    <Text type="secondary">{report.description}</Text>
                  )}
                  {report.segment_id && (
                    <Tag>
                      {report.segment_name || `Сегмент #${report.segment_id}`}
                    </Tag>
                  )}
                </Space>
              </div>
            </Space>
          </Col>
          <Col>
            <Space wrap>
              <Segmented
                value={metric}
                onChange={(v) => dispatch(setMetric(v as ABMetric))}
                options={(report.available_metrics || []).map((m) => ({
                  value: m.value,
                  label: m.label,
                }))}
              />
              <Tooltip title="Обновлять каждые 15 секунд">
                <Space size={4}>
                  <Switch size="small" checked={autoRefresh} onChange={setAutoRefresh} />
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    авто
                  </Text>
                </Space>
              </Tooltip>
              <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>
                Обновить
              </Button>
              <Button
                icon={<DownloadOutlined />}
                loading={exporting}
                onClick={() => handleExport()}
              >
                Выгрузить af_id
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>
      {sendJob && sendJob.status === 'running' && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="Идёт рассылка"
          description={
            <Space direction="vertical" style={{ width: '100%' }}>
              <Text>
                В очередь поставлено {fmtInt(sendJob.queued)} из {fmtInt(sendJob.scanned)}{' '}
                просмотренных · пропущено (уже получали) {fmtInt(sendJob.skipped)}
              </Text>
              <Progress
                percent={
                  sendJob.scanned > 0
                    ? Math.round((sendJob.queued / sendJob.scanned) * 100)
                    : 0
                }
                status="active"
              />
            </Space>
          }
        />
      )}
      {sendJob?.status === 'failed' && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message="Рассылка завершилась с ошибкой"
          description={sendJob.error}
        />
      )}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={12} md={6}>
          <Card size="small">
            <Statistic
              title="Получателей"
              value={report.recipients}
              suffix={
                <Text type="secondary" style={{ fontSize: 12 }}>
                  / {fmtInt(report.total_delivered)} дост.
                </Text>
              }
            />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small">
            <Statistic title="Длительность" value={report.duration_label} />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small">
            <Text type="secondary" style={{ fontSize: 14 }}>
              Набор выборки
            </Text>
            <Progress
              percent={Math.round(report.progress)}
              size="small"
              status={report.progress >= 100 ? 'success' : 'active'}
            />
            <Text type="secondary" style={{ fontSize: 12 }}>
              нужно ~{fmtInt(report.required_per_variant)} на вариант
            </Text>
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small">
            {report.winner ? (
              <Statistic
                title={report.is_significant ? 'Победитель' : 'Лидирует'}
                value={report.winner.name}
                valueStyle={{ color: report.is_significant ? '#52c41a' : '#faad14' }}
                prefix={<TrophyOutlined />}
              />
            ) : (
              <Statistic title="Победитель" value="—" valueStyle={{ color: '#999' }} />
            )}
            <Text type="secondary" style={{ fontSize: 12 }}>
              уверенность {fmtPct(report.confidence_level, 1)}
            </Text>
          </Card>
        </Col>
      </Row>
      <Card
        title="Сравнение вариантов"
        extra={
          <Text type="secondary" style={{ fontSize: 12 }}>
            метрика считается от {report.base === 'delivered' ? 'доставленных' : 'отправленных'}
            {report.variants.length > 2 &&
              ` · порог значимости p < ${report.alpha_adjusted.toFixed(4)}`}
          </Text>
        }
        style={{ marginBottom: 16 }}
      >
        <Table
          columns={columns}
          dataSource={report.variants}
          rowKey="id"
          pagination={false}
          size="middle"
          scroll={{ x: 1240 }}
          rowClassName={(r) => (r.id === report.winner_variant_id ? 'winner-row' : '')}
        />
      </Card>
      <Card title="Контент вариантов">
        <Row gutter={[16, 16]}>
          {report.variants.map((variant) => (
            <Col xs={24} md={12} xl={8} key={variant.id}>
              <Card
                size="small"
                title={
                  <Space>
                    <span
                      style={{
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        background: colorOf(variant.id),
                        display: 'inline-block',
                      }}
                    />
                    {variant.name}
                    {variant.is_control && <Tag>Контроль</Tag>}
                    {report.winner_variant_id === variant.id && (
                      <TrophyOutlined style={{ color: '#faad14' }} />
                    )}
                  </Space>
                }
                style={{
                  height: '100%',
                  borderColor:
                    report.winner_variant_id === variant.id ? '#52c41a' : undefined,
                }}
              >
                <Text strong style={{ display: 'block' }}>
                  {variant.title}
                </Text>
                <Paragraph type="secondary" ellipsis={{ rows: 4, expandable: true }}>
                  {variant.body}
                </Paragraph>
                {variant.click_url && (
                  <div style={{ marginBottom: 8 }}>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Клик ведёт на{' '}
                    </Text>
                    <Text style={{ fontSize: 12 }} copyable ellipsis>
                      {variant.click_url}
                    </Text>
                  </div>
                )}
                {variant.image_url ? (
                  <img
                    src={variant.image_url}
                    alt={variant.name}
                    style={{ maxWidth: '100%', maxHeight: 120, borderRadius: 6 }}
                    onError={(e) => {
                      (e.currentTarget as HTMLImageElement).style.display = 'none';
                    }}
                  />
                ) : (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Без изображения
                  </Text>
                )}
              </Card>
            </Col>
          ))}
        </Row>
      </Card>
      <Drawer
        title="Получатели теста"
        width={Math.min(1100, window.innerWidth - 80)}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        extra={
          <Space>
            <Button icon={<CopyOutlined />} onClick={copyAfIds}>
              Копировать af_id со страницы
            </Button>
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              loading={exporting}
              onClick={() => handleExport(recVariant || undefined)}
            >
              Выгрузить CSV
            </Button>
          </Space>
        }
      >
        <Space wrap style={{ marginBottom: 16 }}>
          <Select
            style={{ width: 220 }}
            value={recVariant}
            onChange={setRecVariant}
            options={[
              { value: 0, label: 'Все варианты' },
              ...report.variants.map((v) => ({ value: v.id, label: v.name })),
            ]}
          />
          <Radio.Group
            value={recStatus}
            onChange={(e) => setRecStatus(e.target.value)}
            optionType="button"
            buttonStyle="solid"
            options={[
              { value: '', label: 'Все' },
              { value: 'delivered', label: 'Доставлен' },
              { value: 'failed', label: 'Ошибка' },
              { value: 'clicked', label: 'Клик' },
              { value: 'converted', label: 'Конверсия' },
            ]}
          />
          <Input.Search
            allowClear
            placeholder="af_id или external_id"
            style={{ width: 280 }}
            value={recSearch}
            onChange={(e) => setRecSearch(e.target.value)}
            onSearch={() => loadRecipients(1)}
          />
        </Space>

        <Table
          columns={recipientColumns}
          dataSource={recipients}
          rowKey="app_user_id"
          loading={recLoading}
          size="small"
          scroll={{ x: 1000 }}
          pagination={{
            current: recPage,
            pageSize: 50,
            total: recTotal,
            showSizeChanger: false,
            showTotal: (t) => `Всего: ${t.toLocaleString('ru-RU')}`,
            onChange: (p) => loadRecipients(p),
          }}
          locale={{
            emptyText: (
              <Empty description="Пока никому не отправляли — запустите рассылку по тесту" />
            ),
          }}
        />
      </Drawer>

      <style>{`
        .winner-row > td { background-color: rgba(82, 196, 26, 0.08) !important; }
      `}</style>
    </div>
  );
};

export default ABTestResults;